import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, ZapIcon, CreditCardIcon, CheckIcon, StarIcon } from 'lucide-react';
interface PricingProps {
  id?: string;
  showUrgency?: boolean;
}
const PLANS = {
  esencial: {
    id: 'esencial',
    name: 'KIT ESENCIAL',
    desc: '8 tomos + 3 bonos',
    oldPrice: '$27.990',
    price: '$19.990',
    url: 'https://checkout.solucionesya.shop/cart/48783993864440:1'
  },
  completa: {
    id: 'completa',
    name: 'EDICIÓN COMPLETA',
    desc: '8 tomos + 3 bonos + Tomos 7 y 8',
    oldPrice: '$35.990',
    price: '$27.990',
    url: 'https://checkout.solucionesya.shop/cart/48737891090680:1'
  }
} as const;
type PlanId = keyof typeof PLANS;
export function Pricing({
  id = 'pricing',
  showUrgency = true
}: PricingProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanId>('completa');
  const plan = PLANS[selectedPlan];
  const handleBuy = () => {
    window.location.href = plan.url;
  };
  return <section id={id} className="py-12 md:py-20 px-4 md:px-8 bg-cream" data-id="element-176">
      <div className="max-w-2xl mx-auto" data-id="element-177">
        <div className="text-center mb-5 md:mb-8 pt-4 md:pt-8" data-id="element-178">
          <h2 className="text-2xl md:text-4xl font-bold font-serif text-teal-900 mb-1 md:mb-2 leading-tight" data-id="element-179">
            Elegí tu Kit
          </h2>
          <p className="text-xs md:text-base text-slate-600" data-id="element-180">
            Ambas opciones incluyen los 3 bonos completos
          </p>
        </div>

        {/* Horizontal stacked plan rows */}
        <div className="space-y-3 md:space-y-4 mb-5 md:mb-6" data-id="element-181">
          {/* Kit Esencial Row */}
          <button type="button" onClick={() => setSelectedPlan('esencial')} className={`w-full text-left rounded-2xl p-4 md:p-5 border-2 transition-all duration-200 flex items-center gap-3 md:gap-4 ${selectedPlan === 'esencial' ? 'border-teal-700 bg-white shadow-lg' : 'border-slate-200 bg-white/60 hover:border-teal-300'}`} data-id="element-182">
            {/* Radio */}
            <div className={`shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selectedPlan === 'esencial' ? 'border-teal-700 bg-teal-700' : 'border-slate-300 bg-white'}`} data-id="element-183">
              {selectedPlan === 'esencial' && <CheckIcon size={14} className="text-white" strokeWidth={3} data-id="element-184" />}
            </div>

            {/* Plan info */}
            <div className="flex-grow min-w-0" data-id="element-185">
              <h3 className="text-sm md:text-base font-bold text-teal-900 leading-tight" data-id="element-186">
                KIT ESENCIAL
              </h3>
              <p className="text-[11px] md:text-xs text-slate-500 font-medium leading-tight mt-0.5" data-id="element-187">
                8 tomos + 3 bonos
              </p>
            </div>

            {/* Price */}
            <div className="shrink-0 text-right" data-id="element-188">
              <div className="text-slate-400 line-through text-[11px] md:text-sm font-medium leading-none" data-id="element-189">
                $27.990
              </div>
              <div className="text-xl md:text-2xl font-bold text-teal-900 leading-tight" data-id="element-190">
                $19.990
              </div>
            </div>
          </button>

          {/* Edición Completa Row */}
          <button type="button" onClick={() => setSelectedPlan('completa')} className={`w-full text-left rounded-2xl p-4 md:p-5 border-2 transition-all duration-200 flex items-center gap-3 md:gap-4 relative ${selectedPlan === 'completa' ? 'border-terracotta bg-teal-900 text-white shadow-xl' : 'border-slate-200 bg-white/60 hover:border-terracotta/50'}`} data-id="element-191">
            {/* Más Elegida badge */}
            <div className="absolute -top-2.5 left-4 md:left-6 bg-terracotta text-white font-bold px-2.5 py-0.5 rounded-full text-[9px] md:text-[10px] shadow uppercase tracking-wider flex items-center gap-1" data-id="element-192">
              <StarIcon size={10} fill="currentColor" data-id="element-193" /> Más Elegida
            </div>

            {/* Ahorro badge */}
            <div className="absolute -top-2.5 right-4 md:right-6 bg-amber-400 text-amber-900 font-bold px-2 py-0.5 rounded-full text-[9px] md:text-[10px] shadow uppercase tracking-wider" data-id="element-194">
              Ahorrás $8.000
            </div>

            {/* Radio */}
            <div className={`shrink-0 w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex items-center justify-center transition-colors mt-1 ${selectedPlan === 'completa' ? 'border-terracotta bg-terracotta' : 'border-slate-300 bg-white'}`} data-id="element-195">
              {selectedPlan === 'completa' && <CheckIcon size={14} className="text-white" strokeWidth={3} data-id="element-196" />}
            </div>

            {/* Plan info */}
            <div className="flex-grow min-w-0 mt-1" data-id="element-197">
              <h3 className={`text-sm md:text-base font-bold leading-tight ${selectedPlan === 'completa' ? 'text-white' : 'text-teal-900'}`} data-id="element-198">
                EDICIÓN COMPLETA
              </h3>
              <p className={`text-[11px] md:text-xs font-medium leading-tight mt-0.5 ${selectedPlan === 'completa' ? 'text-teal-200' : 'text-slate-500'}`} data-id="element-199">
                8 tomos + 3 bonos{' '}
                <span className="font-bold" data-id="element-200">+ Tomos 7 y 8</span>
              </p>
            </div>

            {/* Price */}
            <div className="shrink-0 text-right mt-1" data-id="element-201">
              <div className={`line-through text-[11px] md:text-sm font-medium leading-none ${selectedPlan === 'completa' ? 'text-teal-400' : 'text-slate-400'}`} data-id="element-202">
                $35.990
              </div>
              <div className={`text-xl md:text-2xl font-bold leading-tight ${selectedPlan === 'completa' ? 'text-white' : 'text-teal-900'}`} data-id="element-203">
                $27.990
              </div>
            </div>
          </button>
        </div>

        {/* Shared CTA */}
        <motion.button key={selectedPlan} initial={{
        scale: 0.98
      }} animate={{
        scale: 1
      }} transition={{
        duration: 0.2
      }} onClick={handleBuy} className="w-full bg-terracotta hover:bg-terracotta-hover active:scale-[0.98] text-white font-bold text-base md:text-lg py-4 md:py-5 rounded-2xl shadow-lg transition-all md:hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-204">
          <span data-id="element-205">💥</span>
          <span data-id="element-206">
            COMPRAR {plan.name} · {plan.price}
          </span>
        </motion.button>

        <p className="text-center text-[11px] md:text-xs text-slate-500 mt-3 font-medium leading-snug" data-id="element-207">
          {selectedPlan === 'completa' ? 'La opción más elegida por fonoaudiólogas que quieren el sistema clínico y el consultorio resuelto' : 'Empezá con la evaluación clínica resuelta'}
        </p>

        <div className="mt-6 md:mt-8 flex flex-wrap justify-center items-center gap-x-4 gap-y-2 md:gap-6 text-xs md:text-sm text-slate-500 font-medium" data-id="element-208">
          <div className="flex items-center gap-1.5" data-id="element-209">
            <LockIcon size={14} className="text-teal-700" data-id="element-210" />
            <span data-id="element-211">Pago seguro</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-212">
            <ZapIcon size={14} className="text-teal-700" data-id="element-213" />
            <span data-id="element-214">Acceso en 5 min</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-215">
            <ShieldCheckIcon size={14} className="text-teal-700" data-id="element-216" />
            <span data-id="element-217">Garantía 7 días</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-218">
            <CreditCardIcon size={14} className="text-teal-700" data-id="element-219" />
            <span data-id="element-220">Mercado Pago</span>
          </div>
        </div>
      </div>
    </section>;
}